import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Package, 
  ShoppingCart, 
  Menu,
  X,
  Boxes,
  Sprout,
  Plane,
  Truck,
  ChevronDown,
  ChevronUp,
  Calculator,
  Factory,
  Receipt,
  Settings,
  FileBarChart,
  LogOut
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import SeasonSelector from '../SeasonSelector/SeasonSelector';
import EmailResponseCounter from './EmailResponseCounter';
import { fetchSeasons } from '../../store/slices/seasonSlice';
import { signOut, getCurrentUser } from '../../services/authService';
import { useAbility } from '../../hooks/useAbility';
import './Layout.css';
import './LogoutModal.css';

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [initialLoad, setInitialLoad] = useState(true);
  const [user, setUser] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dispatch = useDispatch();
  const { ability, userRole } = useAbility();

  const toggleSubmenu = (menuName) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const allNavigation = useMemo(() => [
    { name: t('nav.dashboard'), href: '/dashboard', icon: Home, permission: { action: 'read', subject: 'dashboard' } },
    { 
      name: t('nav.customers'), 
      icon: Users,
      permission: { action: 'read', subject: 'customers:management' },
      submenu: [
        { name: 'Gestión de Clientes', href: '/customers', permission: { action: 'read', subject: 'customers:management' } },
        { name: 'Transportes', href: '/customers/transportes', permission: { action: 'read', subject: 'customers:transport' } }
      ]
    },
    { 
      name: t('nav.products'), 
      icon: Package,
      permission: { action: 'read', subject: 'products:management' },
      submenu: [
        { name: 'Catálogo', href: '/products', permission: { action: 'read', subject: 'products:management' } },
        { name: 'Tipo de Caja', href: '/products/tipo-caja', permission: { action: 'read', subject: 'products:boxes' } }
      ]
    },
    { 
      name: t('nav.orders'), 
      icon: ShoppingCart,
      permission: { action: 'read', subject: 'orders:weekly' },
      submenu: [
        { name: 'Semanales', href: '/orders', permission: { action: 'read', subject: 'orders:weekly' } },
        { name: 'Recurrentes', href: '/orders/recurring', permission: { action: 'read', subject: 'orders:recurring' } }
      ]
    },
    { 
      name: t('nav.inventory'), 
      icon: Boxes,
      permission: { action: 'read', subject: 'inventory:management' },
      submenu: [
        { name: 'Disponible', href: '/inventory', permission: { action: 'read', subject: 'inventory:management' } },
        { name: 'Historial', href: '/inventory/history', permission: { action: 'read', subject: 'inventory:history' } }
      ]
    },
    { name: t('nav.productionPlan'), href: '/plan-cosecha', icon: Sprout, permission: { action: 'read', subject: 'production:plan' } },
    { 
      name: 'Exportar', 
      icon: Plane,
      // Check if user has access to any preparativos step or embarque
      customPermissionCheck: (ability) => {
        return ability.can('read', 'export:preparativos:solicitud-embarque') ||
               ability.can('read', 'export:preparativos:coordinar-transporte') ||
               ability.can('read', 'export:preparativos:guia-despacho') ||
               ability.can('read', 'export:preparativos:propuesta-embarque') ||
               ability.can('read', 'export:preparativos:agendamiento-sag') ||
               ability.can('read', 'export:preparativos:impresion-etiquetas') ||
               ability.can('read', 'export:preparativos:solicitud-dus') ||
               ability.can('read', 'export:embarque');
      },
      submenu: [
        { 
          name: 'Preparativos', 
          href: '/export/preparativos',
          customPermissionCheck: (ability) => {
            return ability.can('read', 'export:preparativos:solicitud-embarque') ||
                   ability.can('read', 'export:preparativos:coordinar-transporte') ||
                   ability.can('read', 'export:preparativos:guia-despacho') ||
                   ability.can('read', 'export:preparativos:propuesta-embarque') ||
                   ability.can('read', 'export:preparativos:agendamiento-sag') ||
                   ability.can('read', 'export:preparativos:impresion-etiquetas') ||
                   ability.can('read', 'export:preparativos:solicitud-dus');
          }
        },
        { name: 'Embarque', href: '/export/embarque', permission: { action: 'read', subject: 'export:embarque' } }
      ]
    },
    {
      name: 'Despacho Nacionales',
      href: '/despacho-nacionales',
      icon: Truck,
      permission: { action: 'read', subject: 'despacho-nacionales:view' }
    },
    {
      name: 'Reportes',
      icon: FileBarChart,
      permission: { action: 'read', subject: 'reportes:datos-historicos-embarque:view' },
      submenu: [
        { name: 'Datos Históricos Embarque', href: '/reportes/datos-historicos-embarque', permission: { action: 'read', subject: 'reportes:datos-historicos-embarque:view' } }
      ]
    },
    {
      name: 'Presupuesto',
      icon: Calculator,
      permission: { action: 'read', subject: 'budget:view' },
      submenu: [
        { name: 'Vigente', href: '/presupuesto', permission: { action: 'read', subject: 'budget:view' } },
        { name: 'Archivos', href: '/presupuesto/archivos', permission: { action: 'read', subject: 'budget:files' } }
      ]
    },
    { 
      name: 'Producción', 
      icon: Factory,
      permission: { action: 'read', subject: 'production:view' },
      submenu: [
        { name: 'Vigente', href: '/produccion', permission: { action: 'read', subject: 'production:view' } },
        { name: 'Archivos', href: '/produccion/archivos', permission: { action: 'read', subject: 'production:files' } }
      ]
    },
    { 
      name: 'Gastos', 
      icon: Receipt,
      permission: { action: 'read', subject: 'expenses:view' },
      submenu: [
        { name: 'Vigente', href: '/gastos', permission: { action: 'read', subject: 'expenses:view' } },
        { name: 'Archivos', href: '/gastos/archivos', permission: { action: 'read', subject: 'expenses:files' } }
      ]
    },
    { 
      name: 'Configuración', 
      icon: Settings,
      customPermissionCheck: (ability) => {
        return ability.can('read', 'config') || ability.can('read', 'config:correo');
      },
      submenu: [
        { name: 'Folios', href: '/configuracion/folios', permission: { action: 'read', subject: 'config' } },
        { name: 'Correo', href: '/configuracion/correo', permission: { action: 'read', subject: 'config:correo' } },
        { name: 'OneDrive', href: '/configuracion/onedrive', permission: { action: 'read', subject: 'config' } },
        { name: 'Integraciones', href: '/configuracion/integraciones', permission: { action: 'read', subject: 'config' } },
        { name: 'Pedidos Confirmados', href: '/configuracion/pedidos-confirmados', permission: { action: 'read', subject: 'config' } },
        { name: 'Gestión de Roles', href: '/admin/roles', permission: { action: 'manage', subject: 'all' } }
      ]
    }
  ], [t]);

  const navigation = useMemo(() => {
    return allNavigation.filter(item => {
      // Check custom permission function if provided
      if (item.customPermissionCheck) {
        if (!item.customPermissionCheck(ability)) {
          return false;
        }
      } else if (item.permission && !ability.can(item.permission.action, item.permission.subject)) {
        return false;
      }
      
      if (item.submenu) {
        const filteredSubmenu = item.submenu.filter(subItem => {
          // Check custom permission function for submenu items
          if (subItem.customPermissionCheck) {
            return subItem.customPermissionCheck(ability);
          } else if (subItem.permission) {
            return ability.can(subItem.permission.action, subItem.permission.subject);
          }
          return true;
        });
        
        if (filteredSubmenu.length === 0) {
          return false;
        }
        
        item.submenu = filteredSubmenu;
      }
      
      return true;
    });
  }, [allNavigation, ability]);

  const isActive = (href) => {
    if (href === '/dashboard' && location.pathname === '/') return true;
    return location.pathname === href;
  };

  const isSubmenuActive = useCallback((submenu) => {
    return submenu.some(item => location.pathname === item.href);
  }, [location.pathname]);

  // Auto-abrir el submenú activo solo en la carga inicial
  useEffect(() => {
    if (initialLoad) {
      const activeMenu = navigation.find(item => 
        item.submenu && isSubmenuActive(item.submenu)
      );
      
      if (activeMenu) {
        setOpenSubmenus(prev => ({
          ...prev,
          [activeMenu.name]: true
        }));
      }
      
      setInitialLoad(false);
    }
  }, [location.pathname, initialLoad, navigation, isSubmenuActive]);

  useEffect(() => {
    dispatch(fetchSeasons());
  }, [dispatch]);

  // Load user information
  useEffect(() => {
    const loadUser = async () => {
      const { user: currentUser } = await getCurrentUser();
      setUser(currentUser);
    };
    loadUser();
  }, []);

  // Handle logout button click
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // Confirm logout
  const confirmLogout = async () => {
    setShowLogoutModal(false);
    const { error } = await signOut();
    if (error) {
      console.error('Error signing out:', error);
      alert('Error al cerrar sesión. Por favor intente nuevamente.');
      return;
    }
    navigate('/login');
  };

  // Cancel logout
  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <div className="layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}
        style={{
          backgroundImage: 'url(/images/textura-verde.png)',
          backgroundRepeat: 'repeat',
          backgroundSize: 'auto'
        }}
      >
        <div className="sidebar-header">
          <Link to="/dashboard" className="logo" onClick={() => setSidebarOpen(false)}>
            <img 
              src="/images/Logo.png" 
              alt="Patagonia Vital" 
              className="logo-image"
            />
          </Link>
          <button 
            className="sidebar-close"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navigation.map((item) => {
            const Icon = item.icon;
            
            // Item con submenú
            if (item.submenu) {
              const isSubActive = isSubmenuActive(item.submenu);
              const isOpen = openSubmenus[item.name] || false;
              
              return (
                <div key={item.name}>
                  <button
                    className={`nav-item nav-item-parent ${isSubActive ? 'nav-item-active' : ''}`}
                    onClick={() => toggleSubmenu(item.name)}
                  >
                    <Icon size={20} />
                    <span>{item.name}</span>
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  
                  {isOpen && (
                    <div className="submenu">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={`submenu-item ${isActive(subItem.href) ? 'submenu-item-active' : ''}`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <span>{subItem.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            
            // Item normal sin submenú
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-item ${isActive(item.href) ? 'nav-item-active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              <span>{user?.email?.charAt(0).toUpperCase() || 'U'}</span>
            </div>
            <div className="user-details">
              <p className="user-name">{user?.email || 'Usuario'}</p>
              <p className="user-role">{userRole || 'Administrador'}</p>
            </div>
          </div>
          <button 
            className="logout-button"
            onClick={handleLogoutClick}
            title="Cerrar sesión"
          >
            <LogOut size={18} strokeWidth={2} />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="main-content">
        {/* Header */}
        <header 
          className="header"
          style={{
            backgroundImage: 'url(/images/textura-gris.png)',
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto'
          }}
        >
          <button 
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={24} />
          </button>
          
          <div className="header-title">
            <h2>
              {(() => {
                // Caso especial para rutas de preparativos
                if (location.pathname.startsWith('/export/preparativos')) {
                  return 'PREPARATIVOS EMBARQUE';
                }
                
                // Buscar en items principales
                const mainItem = navigation.find(item => item.href && isActive(item.href));
                if (mainItem) return mainItem.name;
                
                // Buscar en submenús
                for (const item of navigation) {
                  if (item.submenu) {
                    const subItem = item.submenu.find(sub => isActive(sub.href));
                    if (subItem) return `${item.name} - ${subItem.name}`;
                  }
                }
                
                // Rutas especiales de tareas de embarque
                if (location.pathname === '/export/embarque/peso-gross') {
                  return 'Peso Gross';
                }
                
                if (location.pathname === '/export/embarque/tarifas-anuales') {
                  return 'Tarifas del Embarque';
                }
                
                if (location.pathname === '/export/embarque/tipo-cambio') {
                  return 'Tipo de Cambio';
                }
                
                // La ruta conserva el nombre 'costos-estimados'; el nombre visible es "Costos".
                if (location.pathname === '/export/embarque/costos-estimados') {
                  return 'Costos';
                }
                
                return t('nav.dashboard');
              })()}
            </h2>
          </div>

          <div className="header-actions">
            <EmailResponseCounter />
            <SeasonSelector />
          </div>
        </header>

        {/* Page content */}
        <main className="page-content">
          {children}
        </main>
      </div>

      {/* Modal de Confirmación de Logout */}
      {showLogoutModal && (
        <div className="logout-modal-overlay" onClick={cancelLogout}>
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logout-modal-icon">
              <LogOut size={32} strokeWidth={2} />
            </div>
            <h3>Cerrar Sesión</h3>
            <p>¿Está seguro que desea cerrar sesión?</p>
            <div className="logout-modal-actions">
              <button className="logout-btn-cancel" onClick={cancelLogout}>
                Cancelar
              </button>
              <button className="logout-btn-confirm" onClick={confirmLogout}>
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
